import { useIdentity } from "IdentityProvider";
import { api } from "services/rpc/RpcProvider";
import { canPick, RESOURCE_TYPES, SupportResource } from "./resources";

// The page behind the drawer has usually loaded these already, so they mostly hit the cache
const lookup = (enabled: boolean) => ({ enabled, retry: false, refetchOnWindowFocus: false });

// Name of an auto-attached resource; steps are shown as "Parent › Step" like in the picker
export const useResourceLabel = (resource?: SupportResource) => {
  const { user } = useIdentity();
  const allowed = !!resource && canPick(user, resource.type);
  const is = (...types: SupportResource["type"][]) => allowed && types.includes(resource!.type);

  const workflowId =
    resource?.type === "workflow"
      ? resource.id
      : resource?.type === "workflowStep"
      ? resource.workflowId
      : "";
  const automationId =
    resource?.type === "automation"
      ? resource.id
      : resource?.type === "automationStep"
      ? resource.automationId
      : "";
  const id = resource?.id ?? "";

  const workflow = api.workflows.v4.find.useQuery(
    { id: workflowId },
    lookup(is("workflow", "workflowStep"))
  );
  const workflowStep = api.workflows.v4.steps.find.useQuery(
    { stepId: id },
    lookup(is("workflowStep"))
  );
  const automation = api.automations.find.useQuery(
    { id: automationId },
    lookup(is("automation", "automationStep"))
  );
  const automationStep = api.automations.steps.find.useQuery({ id }, lookup(is("automationStep")));
  const trigger = api.triggers.find.useQuery({ id }, lookup(is("trigger")));
  // Automations are shown under their employee, like in the picker
  const employeeId = resource?.type === "employee" ? id : automation.data?.employeeId ?? "";
  const employee = api.employees.v4.find.useQuery(
    { id: employeeId },
    lookup(!!employeeId && is("employee", "automation", "automationStep"))
  );
  const form = api.forms.v2.find.useQuery({ id }, lookup(is("form")));

  if (!resource) return undefined;

  const path = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(" › ");

  const name = {
    workflow: workflow.data?.title,
    workflowStep: path(workflow.data?.title, workflowStep.data?.stepTitle),
    automation: path(employee.data?.name, automation.data?.title),
    automationStep: path(employee.data?.name, automation.data?.title, automationStep.data?.title),
    trigger: trigger.data?.name,
    employee: employee.data?.name,
    form: form.data?.title
  }[resource.type];

  return name || RESOURCE_TYPES[resource.type].title;
};
